import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CompteARebourd from '../CompteARebourd/CompteARebourd';
import './SuiviHeader.css';
import { getStatusColor } from '../../utils/entityUtils';

interface SuiviHeaderProps {
  titre: string;
  sousTitre: string;
  subtitle: string;
  status: string;
  statusColor: string;
  delais?: string;
  backgroundImage?: string;
  noNavigation?: boolean;
  statusId?: number;
  source?: string; // BC, MS, ou BCI (Paniers importés)
  onRetour?: () => void;
}

const SuiviHeader: React.FC<SuiviHeaderProps> = ({
  titre,
  sousTitre,
  subtitle,
  status,
  statusColor,
  delais,
  backgroundImage,
  noNavigation,
  statusId,
  source,
  onRetour,
}) => {
  const navigate = !noNavigation ? useNavigate() : null;

  // NOUVEAU: Fonction pour vérifier si la date est déjà expirée
  const isDateExpired = (dateStr: string): boolean => {
    if (!dateStr) return false;

    const [datePart, timePart] = dateStr.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute, second] = timePart.split(':').map(Number);
    const targetDate = new Date(year, month - 1, day, hour, minute, second);
    const now = new Date();

    return now.getTime() >= targetDate.getTime();
  };

  // NOUVEAU: Calculer si expiré par statut (en dehors pour éviter la recréation)
  const checkExpiredByStatus = (src?: string, stId?: number, delaisValue?: string): boolean => {
    // Ne considérer comme expiré que si on a une date d'expiration
    if (!delaisValue) return false;
    // CORRECTION: Exclure les BCI (source === 'BCI') de la logique d'expiration
    if (src === 'BCI') return false;
    return (src === 'MS' && stId === 12) || (src === 'BC' && stId === 9);
  };

  // NOUVEAU: Déterminer le statut initial en fonction de la date ou du statut
  const getInitialStatus = () => {
    if (checkExpiredByStatus(source, statusId, delais) || (delais && source !== 'BCI' && isDateExpired(delais))) {
      return 'Expiré';
    }
    return status;
  };

  const getInitialStatusColor = () => {
    if (checkExpiredByStatus(source, statusId, delais) || (delais && source !== 'BCI' && isDateExpired(delais))) {
      return getStatusColor('Expiré');
    }
    return getStatusColor(statusColor);
  };

  const [currentStatus, setCurrentStatus] = useState(getInitialStatus());
  const [currentStatusColor, setCurrentStatusColor] = useState(getInitialStatusColor());
  const [isExpired, setIsExpired] = useState(checkExpiredByStatus(source, statusId, delais) || (delais && source !== 'BCI' ? isDateExpired(delais) : false));

  // NOUVEAU: Écouter l'événement d'expiration du chrono
  useEffect(() => {
    const handleChronoExpired = () => {
      console.log('📢 Événement chronoExpired reçu - Mise à jour du statut');
      setCurrentStatus('Expiré');
      setCurrentStatusColor(getStatusColor('Expiré'));
      setIsExpired(true);
    };

    window.addEventListener('chronoExpired', handleChronoExpired);

    return () => {
      window.removeEventListener('chronoExpired', handleChronoExpired);
    };
  }, []);

  // Mettre à jour le statut si les props changent
  useEffect(() => {
    const expiredByStatus = checkExpiredByStatus(source, statusId, delais);
    const expiredByDate = delais && source !== 'BCI' ? isDateExpired(delais) : false;

    console.log('🔍 SuiviHeader - Vérification expiration:', {
      source,
      statusId,
      expiredByStatus,
      expiredByDate,
      delais,
      isBCI: source === 'BCI'
    });

    if (expiredByStatus || expiredByDate) {
      setCurrentStatus('Expiré');
      setCurrentStatusColor(getStatusColor('Expiré'));
      setIsExpired(true);
    } else {
      setCurrentStatus(status);
      setCurrentStatusColor(statusColor);
      setIsExpired(false);
    }
  }, [status, statusColor, delais, statusId, source]);

  // Fonction pour normaliser le statut en classe CSS
  const getStatusClassName = (statusValue: string | null | undefined): string => {
    if (!statusValue) return 'status-unknown';

    return `status-${statusValue
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // supprime les accents
      .replace(/[^a-z0-9]+/g, '-')     // remplace tout ce qui n'est pas lettre/chiffre par un tiret
      .replace(/^-+|-+$/g, '')         // supprime les tirets en début/fin
      }`;
  };

  return (
    <div
      className="suivi-commande-title"
      style={{
        backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(${backgroundImage})`
      }}
    >
      {!noNavigation && navigate && (
        <button className="back-button no-print" onClick={() => onRetour ? onRetour() : navigate(-1)} aria-label="Retour">
          <svg xmlns="http://www.w3.org/2000/svg" width="45" height="45" viewBox="0 0 55 55" fill="none">
            <circle cx="27.5" cy="27.5" r="27.5" fill="#FFFDFC" />
            <path
              d="M34.7369 13.0263L20.2632 27.5L34.7369 41.9737"
              stroke="#2C3948"
              strokeWidth="4"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}

      <h1 title={titre}>{titre}</h1>
      <h2>{sousTitre}</h2>

      <div className="subtitle">
        <span>{subtitle}</span>
      </div>

      <div className="status-container">
        <div className="status-header">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 45 41" fill="none">
            <ellipse cx="22.5" cy="20.5" rx="22.5" ry="20.5" fill={currentStatusColor} />
          </svg>
          <span className={`status-text ${getStatusClassName(currentStatus)}`} style={{ color: currentStatusColor }}>
            {currentStatus === 'Refuse' || currentStatus === 'Refuser'
              ? 'Refusé'
              : currentStatus === 'Valide' || currentStatus === 'Valider'
                ? 'Accepté'
                : currentStatus === 'Expire' || currentStatus === 'Expirer'
                  ? 'Expiré'
                  : currentStatus || 'Statut inconnu'}
          </span>

          {/* NOUVEAU: N'afficher le chrono que si la date n'est pas encore expirée */}
          {delais && !isExpired && (
            <span className="countdown-container">
              <CompteARebourd targetDate={delais} source={source} />
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuiviHeader;